import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const PROJECTS = [
  {
    title: "Background Design",
    desc: "Custom tech-themed background visuals.",
    img: "/images/data1.jpg",
    link: "https://dawdul99.blogspot.com/2025/03/backraunt-imag.html",
    category: "Design",
    tags: ["Visuals", "Branding"],
  },
  {
    title: "Virtual Assistant",
    desc: "AI-powered sound-based virtual assistant.",
    img: "/images/data2.jpg",
    link: "https://dawdul99.blogspot.com/2025/02/i-will-be-your-sound-virtual-assistant.html",
    category: "AI",
    tags: ["Voice", "Automation"],
  },
  {
    title: "Data Entry & Web Research",
    desc: "Reliable and accurate data management.",
    img: "/images/data3.jpg",
    link: "https://dawdul99.blogspot.com/2025/02/i-will-do-perfect-data-entry-web.html",
    category: "Data",
    tags: ["Ops", "ETL"],
  },
  {
    title: "YouTube Thumbnail Design",
    desc: "Creative thumbnails for growth.",
    img: "/images/data4.jpg",
    link: "https://dawdul99.blogspot.com/2025/02/thumbnail-design-for-biggest-youtube.html",
    category: "Design",
    tags: ["Creator", "Growth"],
  },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Work() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const TOKENS = useMemo(
    () =>
      isDark
        ? {
            text: "#FFFFFF",
            muted: "rgba(255,255,255,.78)",
            panel: "rgba(12,18,30,.86)",
            ring: "rgba(255,255,255,.10)",
            shadow: "0 36px 80px rgba(0,0,0,.45)",
          }
        : {
            text: "#0B1220",
            muted: "rgba(17,24,39,.72)",
            panel: "#FFFFFF",
            ring: "rgba(2,6,23,.10)",
            shadow: "0 28px 64px rgba(2,6,23,.14)",
          },
    [isDark]
  );

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [preview, setPreview] = useState(null);
  const cats = useMemo(() => ["All", ...Array.from(new Set(PROJECTS.map(p => p.category)))], []);

  const filtered = useMemo(() => {
    const base = cat === "All" ? PROJECTS : PROJECTS.filter(p => p.category === cat);
    const term = q.trim().toLowerCase();
    if (!term) return base;
    return base.filter(p =>
      p.title.toLowerCase().includes(term) ||
      p.desc.toLowerCase().includes(term) ||
      p.tags.some(t => t.toLowerCase().includes(term))
    );
  }, [q, cat]);

  return (
    <section className="relative min-h-[70vh] font-inter">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Our <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }}>work</span>
          </h1>
          <p className="mt-3 text-lg" style={{ color: TOKENS.muted }}>
            Real projects. Real impact. (10+ live projects currently)
          </p>
        </div>

        {/* controls */}
        <div className="mt-8 flex flex-col lg:flex-row gap-3 lg:gap-4 items-center">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search work… (e.g., design, voice)"
            className="w-full lg:max-w-md rounded-xl px-4 py-3 outline-none ring-1"
            style={{ background: isDark ? "rgba(255,255,255,.06)" : "#fff", borderColor: TOKENS.ring, color: TOKENS.text }}
          />
          <nav className="flex flex-wrap gap-2 lg:ml-auto" aria-label="Category">
            {cats.map((c) => {
              const active = cat === c;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className="px-4 py-2 rounded-full text-sm font-semibold ring-1 transition"
                  style={{
                    borderColor: TOKENS.ring,
                    background: active ? "linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))" : (isDark ? "rgba(255,255,255,.06)" : "#fff"),
                    color: active ? "#000" : TOKENS.muted,
                  }}
                >
                  {c}
                </button>
              );
            })}
          </nav>
        </div>

        {/* grid */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.04 }}
              className="relative rounded-2xl p-[1px]"
              style={{
                background:
                  "conic-gradient(from 90deg at 50% 50%, rgba(16,185,129,.35), rgba(6,182,212,.35), transparent 60%)",
                boxShadow: TOKENS.shadow,
              }}
            >
              <div
                className="rounded-2xl h-full overflow-hidden ring-1"
                style={{ background: TOKENS.panel, borderColor: TOKENS.ring }}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                    onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/800x500?text=Preview")}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 text-[11px] rounded-full font-semibold ring-1"
                          style={{ borderColor: TOKENS.ring, background: isDark ? "rgba(255,255,255,.06)" : "rgba(2,6,23,.04)" }}>
                      {p.category}
                    </span>
                    {p.tags.map((t) => (
                      <span key={t} className="px-2 py-0.5 text-[11px] rounded-full ring-1"
                            style={{ borderColor: TOKENS.ring, color: TOKENS.muted }}>
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-lg font-semibold" style={{ color: TOKENS.text }}>{p.title}</h3>
                  <p className="text-sm mt-1" style={{ color: TOKENS.muted }}>{p.desc}</p>
                  <div className="mt-4 flex gap-2">
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-black"
                      style={{ background: "linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }}
                    >
                      Visit
                    </a>
                    <button
                      onClick={() => setPreview(p)}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold ring-1"
                      style={{ borderColor: TOKENS.ring, color: TOKENS.text }}
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* lightbox */}
        <AnimatePresence>
          {preview && (
            <motion.div className="fixed inset-0 z-50"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="absolute inset-0 bg-black/60" onClick={() => setPreview(null)} />
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="relative mx-auto mt-12 max-w-4xl w-[92vw] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-black"
              >
                <img src={preview.img} alt={preview.title} className="w-full max-h-[70vh] object-contain" />
                <div className="p-4 sm:p-5 bg-white/5 backdrop-blur">
                  <h4 className="text-white text-lg font-semibold">{preview.title}</h4>
                  <p className="text-white/80 text-sm mt-1">{preview.desc}</p>
                  <div className="mt-4 flex gap-2">
                    <a href={preview.link} target="_blank" rel="noopener noreferrer"
                       className="px-4 py-2 rounded-lg text-sm font-semibold text-black"
                       style={{ background: "linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }}>
                      Visit project
                    </a>
                    <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-lg text-sm font-semibold ring-1 ring-white/30 text-white">Close</button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`.font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}`}</style>
    </section>
  );
}
